import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { TableRows, Map as MapIcon, Bolt, ArrowOutward, Gavel } from '@mui/icons-material';
import { getUpcomingMatchById, getVetoStatsForTeamBySeason, getTeamStanding } from '../../services/MatchService';
import { getPlayersByTeamIdLatestFirst } from '../../services/PlayerService';
import { fetchTeamStats } from '../../services/TeamService';
import { getMatchesForAnalysis } from '../../services/AnalysisService';
import { currentSeason } from '../../Constants';
import Loading from '../../components/Loading';
import PlayerImage from '../../components/PlayerImage';
import TeamLogo from '../../components/TeamLogo';
import { UpcomingMatch } from '../../models/UpcomingMatch';
import { PlayerStats } from '../../models/PlayerStats';
import { MapStats } from '../../models/MapStats';
import { TeamMatchStats } from '../../models/TeamMatchStats';
import { Standing } from '../../models/Standing';
import { useLiveMatch } from '../../services/LiveService';
import { PreMatch2Header, pct } from './PreMatch2Shared';
import PreMatch2Maps, { MapRecord } from './PreMatch2Maps';
import PreMatch2Veto from './PreMatch2Veto';
import styles from './PreMatch2.module.css';

type Tab = 'overview' | 'maps' | 'veto';
const TABS: Tab[] = ['overview', 'maps', 'veto'];

// The tale of the tape. Each row is [label, won, total] per side; every number comes from
// team_match_stats, which is populated from season 31 onward only.
const totRows = (s: TeamMatchStats | null): { label: string; won: number; total: number }[] => {
    if (!s) return [];
    const openings = s.roundsWithFirstKill + s.roundsWithFirstDeath;
    return [
        { label: 'Runde-%', won: s.roundsWon, total: s.roundsPlayed },
        { label: 'Pistol', won: s.pistolRoundsWon, total: s.pistolRoundsPlayed },
        { label: 'Åbningsduel', won: s.roundsWithFirstKill, total: openings },
        { label: 'Efter first kill', won: s.roundsWonAfterFirstKill, total: s.roundsWithFirstKill },
        { label: 'Efter eget plant', won: s.roundsWonAfterTeamPlant, total: s.bombPlantsForTeam },
        { label: 'Retake', won: s.roundsWonAfterEnemyPlant, total: s.bombPlantsAgainstTeam },
        { label: 'T-side', won: s.tSideRoundsWon, total: s.tSideRoundsPlayed },
        { label: 'CT-side', won: s.ctSideRoundsWon, total: s.ctSideRoundsPlayed },
    ];
};

const Lineup: React.FC<{ players: PlayerStats[] }> = ({ players }) => {
    // One rating system for the whole column rather than a per-player mix: if anyone on the
    // roster has a Rating 3.0 the column is R3.0 and a player without one shows a dash, which
    // is honest. Falling back row by row would put two different scales under one heading.
    const useR3 = players.some(p => typeof p.rating30 === 'number');
    return (
        <>
            <div className={styles.lineupCols}>
                <span>Spiller</span>
                <span>{useR3 ? 'Rating 3.0' : 'Rating 2.1'}</span>
            </div>
            <div className={styles.lineup}>
                {players.map((p) => {
                    const rating = useR3 ? p.rating30 : p.rating2;
                    const meta = [
                        p.account?.role || 'Rifler',
                        p.account?.age ? String(p.account.age) : null,
                        // Sample size beside every rating, so a number built on one map reads
                        // as one map without the page having to editorialise about it.
                        p.gamesPlayed > 0 ? `${p.gamesPlayed} maps` : 'ingen maps',
                    ].filter(Boolean).join(' · ');
                    return (
                        <Link to={`/player/${p.steamID}`} className={styles.player} key={p.steamID}>
                            <PlayerImage id={p.steamID} width={32} />
                            <div>
                                <div className={styles.playerName}>{p.account?.playerName ?? p.steamID}</div>
                                <div className={styles.playerMeta}>{meta}</div>
                            </div>
                            <span className={`${styles.playerRating} ${rating ? '' : styles.playerRatingNone}`}>
                                {rating ? rating.toFixed(2) : '–'}
                            </span>
                        </Link>
                    );
                })}
            </div>
        </>
    );
};

/**
 * /prematch/:matchId — the prematch page rebuilt on MatchPage2's shell.
 */
const PreMatchPage2: React.FC = () => {
    const { matchId } = useParams<{ matchId: string }>();
    const { match: live, loaded: liveLoaded } = useLiveMatch(matchId);
    // The wait for the feed is bounded: if the socket never answers, fall through to /match
    // after a few seconds instead of leaving a spinner up.
    const [liveWaitOver, setLiveWaitOver] = useState(false);
    useEffect(() => {
        const timer = setTimeout(() => setLiveWaitOver(true), 3000);
        return () => clearTimeout(timer);
    }, [matchId]);
    const [searchParams, setSearchParams] = useSearchParams();
    const [tab, setTab] = useState<Tab>(() => TABS.find(t => t === searchParams.get('tab')) ?? 'overview');

    const [loading, setLoading] = useState(true);
    const [played, setPlayed] = useState(false);
    const [match, setMatch] = useState<UpcomingMatch | null>(null);
    const [rosters, setRosters] = useState<Record<string, PlayerStats[]>>({});
    const [stats, setStats] = useState<Record<string, TeamMatchStats | null>>({});
    const [vetos, setVetos] = useState<Record<string, MapStats[]>>({});
    const [standings, setStandings] = useState<Record<string, Standing | null>>({});
    // teamId -> map -> won/played this season and all-time, for the veto board's winrate column.
    const [records, setRecords] = useState<Record<string, Map<string, MapRecord>>>({});

    useEffect(() => {
        if (!matchId) return;
        let cancelled = false;
        (async () => {
            try {
                const data = await getUpcomingMatchById(matchId);
                if (cancelled) return;
                // Gone from the upcoming feed means it has been played; /match owns it from there.
                if (!data) { setPlayed(true); return; }
                setMatch(data);
            } catch {
                if (!cancelled) setPlayed(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [matchId]);

    const loadTeam = useCallback(async (teamId: string) => {
        const [roster, teamStats, veto, standing, maps] = await Promise.all([
            getPlayersByTeamIdLatestFirst(teamId, currentSeason, true).catch(() => []),
            fetchTeamStats(teamId, currentSeason).catch(() => null),
            getVetoStatsForTeamBySeason(teamId, String(currentSeason)).catch(() => []),
            getTeamStanding(teamId, currentSeason).catch(() => null),
            // Every map the team has ever played. The maps tab leads with this season's slice and
            // keeps the all-time total as context, so one call serves both.
            getMatchesForAnalysis(teamId, '', []),
        ]);
        const record = new Map<string, MapRecord>();
        maps.forEach(m => {
            const key = m.mapName.replace('de_', '').toLowerCase();
            // Use the id the rows came back keyed by: a team that registered twice resolves to
            // its canonical id server-side, and comparing against the requested one would read
            // every scoreline from the opponent's side.
            const subject = m.subjectTeamId ?? teamId;
            const us = m.team1Id === subject ? m.team1score : m.team2score;
            const them = m.team1Id === subject ? m.team2score : m.team1score;
            const won = us > them ? 1 : 0;
            const thisSeason = m.season === currentSeason ? 1 : 0;
            const cur = record.get(key) ?? { won: 0, played: 0, seasonWon: 0, seasonPlayed: 0 };
            record.set(key, {
                won: cur.won + won,
                played: cur.played + 1,
                seasonWon: cur.seasonWon + won * thisSeason,
                seasonPlayed: cur.seasonPlayed + thisSeason,
            });
        });
        return { teamId, roster: roster.slice(0, 5), teamStats, veto, standing, record };
    }, []);

    useEffect(() => {
        if (!match) return;
        let cancelled = false;
        (async () => {
            const results = await Promise.all([
                loadTeam(match.team1.teamId),
                loadTeam(match.team2.teamId),
            ]);
            if (cancelled) return;
            setRosters(Object.fromEntries(results.map(r => [r.teamId, r.roster])));
            setStats(Object.fromEntries(results.map(r => [r.teamId, r.teamStats])));
            setVetos(Object.fromEntries(results.map(r => [r.teamId, r.veto])));
            setStandings(Object.fromEntries(results.map(r => [r.teamId, r.standing])));
            setRecords(Object.fromEntries(results.map(r => [r.teamId, r.record])));
        })();
        return () => { cancelled = true; };
    }, [match, loadTeam]);

    const setTabAndUrl = (next: Tab) => {
        setTab(next);
        const params = new URLSearchParams(searchParams);
        if (next === 'overview') params.delete('tab'); else params.set('tab', next);
        setSearchParams(params, { replace: true });
    };

    // Wait for the live feed's first answer so a live match isn't sent to /match, which has
    // nothing until the demo is parsed.
    if (loading || (played && !liveLoaded && !liveWaitOver)) {
        return <div className="container text-center text-light"><Loading /></div>;
    }
    if (played && matchId) return <Navigate to={live ? `/live/${matchId}` : `/match/${matchId}`} replace />;
    if (!match) return <div className={styles.notice}><h2>Kampen findes ikke</h2><p>Der er ingen kommende kamp med det id.</p></div>;

    const t1 = match.team1;
    const t2 = match.team2;
    const rows1 = totRows(stats[t1.teamId] ?? null);
    const rows2 = totRows(stats[t2.teamId] ?? null);
    const st1 = standings[t1.teamId];
    const st2 = standings[t2.teamId];

    return (
        <div className={styles.page}>
            <PreMatch2Header match={match} live={live} />

            <div className={styles.tabs}>
                <button
                    className={`${styles.tab} ${tab === 'overview' ? styles.tabActive : ''}`}
                    onClick={() => setTabAndUrl('overview')}
                    type="button"
                >
                    <TableRows sx={{ fontSize: 19 }} />Oversigt
                </button>
                <button
                    className={`${styles.tab} ${tab === 'maps' ? styles.tabActive : ''}`}
                    onClick={() => setTabAndUrl('maps')}
                    type="button"
                >
                    <MapIcon sx={{ fontSize: 19 }} />Maps
                </button>
                <button
                    className={`${styles.tab} ${tab === 'veto' ? styles.tabActive : ''}`}
                    onClick={() => setTabAndUrl('veto')}
                    type="button"
                >
                    <Gavel sx={{ fontSize: 19 }} />Veto
                </button>
                <Link to={`/prematch/${matchId}/prep`} className={styles.cta}>
                    <Bolt sx={{ fontSize: 17 }} />
                    Forbered kampen
                    <ArrowOutward sx={{ fontSize: 13 }} className={styles.ctaArrow} />
                </Link>
            </div>

            {tab === 'overview' && (
                <div className={styles.overviewGrid}>
                    <div className={styles.panel}>
                        <div className={styles.panelHead}>
                            <span className={styles.panelTitle}>
                                <TeamLogo logo={t1.teamLogo} name={t1.teamName} className={styles.crestSm} />
                                {t1.teamName}
                            </span>
                            {st1 && <span className={styles.panelSub}>{st1.rank}. plads</span>}
                        </div>
                        <Lineup players={rosters[t1.teamId] ?? []} />
                    </div>

                    <div className={styles.panel}>
                        <div className={styles.panelHead}>
                            <span className={styles.panelTitle}>Tale of the tape</span>
                            <span className={styles.panelSub}>Sæson {currentSeason} · alle maps</span>
                        </div>
                        <div className={styles.panelBody}>
                            {rows1.length === 0 && rows2.length === 0 ? (
                                <p className={styles.panelSub} style={{ margin: 0 }}>
                                    Ingen runde-statistik for sæson {currentSeason} endnu.
                                </p>
                            ) : (
                                <div className={styles.tot}>
                                    {rows1.map((row, i) => {
                                        const other = rows2[i];
                                        const a = pct(row.won, row.total);
                                        const b = other ? pct(other.won, other.total) : null;
                                        return (
                                            <div className={styles.totRow} key={row.label}>
                                                <div className={`${styles.totValue} ${styles.totValueLeft} ${a !== null && b !== null && a > b ? styles.totLead : ''}`}>
                                                    {a === null ? '–' : `${a}%`}
                                                </div>
                                                <div className={`${styles.bar} ${styles.barLeft}`}><i style={{ width: `${a ?? 0}%` }} /></div>
                                                <div className={styles.totLabel}>{row.label}</div>
                                                <div className={`${styles.bar} ${styles.barRight}`}><i style={{ width: `${b ?? 0}%` }} /></div>
                                                <div className={`${styles.totValue} ${b !== null && a !== null && b > a ? styles.totLead : ''}`}>
                                                    {b === null ? '–' : `${b}%`}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className={styles.panel}>
                        <div className={styles.panelHead}>
                            <span className={styles.panelTitle}>
                                <TeamLogo logo={t2.teamLogo} name={t2.teamName} className={styles.crestSm} />
                                {t2.teamName}
                            </span>
                            {st2 && <span className={styles.panelSub}>{st2.rank}. plads</span>}
                        </div>
                        <Lineup players={rosters[t2.teamId] ?? []} />
                    </div>
                </div>
            )}

            {tab === 'maps' && (
                <PreMatch2Maps team1={t1} team2={t2} vetos={vetos} records={records} />
            )}

            {tab === 'veto' && (
                <PreMatch2Veto team1={t1} team2={t2} division={match.division} rosters={rosters} records={records} />
            )}
        </div>
    );
};

export default PreMatchPage2;
